import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import "@/App.css";
import {
  Phone, Mail, MapPin, CheckCircle, Star, ChevronDown, Building2, Monitor,
  Sparkles, Shield, GraduationCap, Package, FileText, Users, Target,
  TrendingUp, Award, ArrowRight, Menu, X, Bot, Workflow, Zap, Euro,
  Send, Loader2, Search, ListChecks, Rocket, MessageSquare,
  Repeat, BarChart3, PenLine, Inbox, BadgeCheck,
  Landmark, Globe, Coins, Handshake, AlertTriangle, Banknote,
  Sun, Plug, ClipboardCheck, HardHat,
} from "lucide-react";
import {
  WEB3FORMS_KEY, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_INTL,
  SOCIETE, INTERVENANT, CERTIFICATION_AO, OFFRE_SUBVENTIONS, OFFRE_QUALIFICATIONS, estComplet,
} from "@/config";
import {
  SUBVENTIONS_ACCROCHE, REGLE_ANTERIORITE, NIVEAUX, OPERATEURS_INTRO, OPERATEURS,
  ETAPES as ETAPES_SUBVENTIONS, RESERVES, ENGAGEMENTS,
  OFFRE as OFFRE_SUBV_CONTENU, FAQ as FAQ_SUBVENTIONS,
} from "@/data/subventions";
import {
  ACCROCHE as QUALIF_ACCROCHE, CONSTAT, FAMILLES, FREIN,
  ETAPES as ETAPES_QUALIF, RESERVES as RESERVES_QUALIF,
  ENGAGEMENTS as ENGAGEMENTS_QUALIF, OFFRE as OFFRE_QUALIF_CONTENU,
  FAQ as FAQ_QUALIFICATIONS, TITRE_SECTION as TITRE_QUALIF,
  CARTE_PONT as CARTE_PONT_QUALIF, CAS_DE_FIGURE_TITRE, CAS_DE_FIGURE,
  CTA_BANNIERE as CTA_BANNIERE_QUALIF,
  GUIDES,
} from "@/data/qualifications";

// Résolution des clés d'icônes de src/data/subventions.js
const ICONES_SUBVENTIONS = {
  building: Building2, map: MapPin, landmark: Landmark, globe: Globe,
  search: Search, list: ListChecks, file: FileText, handshake: Handshake, coins: Coins,
};

// Résolution des clés d'icônes de src/data/qualifications.js
const ICONES_QUALIFICATIONS = {
  building: Building2, sun: Sun, plug: Plug, clipboard: ClipboardCheck,
  search: Search, inbox: Inbox, file: FileText, repeat: Repeat,
};

const OBJETS = [
  "Réponse à un appel d'offres",
  "Recherche de subventions",
  "Qualification professionnelle",
  "Formation IA / agents sur-mesure",
  "Financement OPCO",
  "Plusieurs sujets / Autre",
];

function App() {
  const [openFaq, setOpenFaq] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [metiersOpen, setMetiersOpen] = useState(false);
  const metiersRef = useRef(null);

  // Le menu « Nos métiers » se ferme au clic extérieur et à la touche Échap.
  useEffect(() => {
    if (!metiersOpen) return undefined;
    const onClick = (e) => {
      if (metiersRef.current && !metiersRef.current.contains(e.target)) setMetiersOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMetiersOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [metiersOpen]);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [offreTab, setOffreTab] = useState("ao");

  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", sector: "", consent: false,
  });
  const [rdvFormData, setRdvFormData] = useState({
    objet: OBJETS[0], name: "", email: "", phone: "", company: "", message: "", consent: false,
  });

  // { form: 'devis'|'rdv', state: 'sending'|'sent'|'mailto'|'error', message: string }
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const scrollToSection = useCallback((sectionId, objet) => {
    if (objet) setRdvFormData((d) => ({ ...d, objet }));
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  }, []);

  // --- Défilement vers l'ancre au chargement -------------------------------
  // Le site est une SPA mono-page, mais nos pages statiques de référencement
  // (frontend/public/*.html) renvoient vers des ancres de l'accueil, par ex.
  // /#qualifications ou /#subventions. Ces liens ne passent jamais par
  // scrollToSection (qui ne gère que les clics internes) : sans ce useEffect,
  // window.location.hash n'est jamais lu et le visiteur atterrit en haut de
  // l'accueil au lieu de la section visée.
  // Tableau de dépendances vide : ne s'exécute qu'au montage, une seule fois.
  useEffect(() => {
    if (!window.location.hash) return;

    // Une ancre peut arriver encodée (ex. accents) : on la décode avant de
    // chercher l'élément. Un hash malformé (% invalide) ne doit rien casser.
    let sectionId;
    try {
      sectionId = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return;
    }
    if (!sectionId) return;

    // Au moment où cet effet s'exécute (juste après le commit React), le DOM
    // du premier rendu vient tout juste d'être posé et n'a pas forcément
    // encore été peint par le navigateur : un getElementById immédiat peut
    // rater l'élément ou le trouver avant sa mise en page finale. On attend
    // la prochaine peinture via requestAnimationFrame avant de défiler.
    const frameId = requestAnimationFrame(() => {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    });

    return () => cancelAnimationFrame(frameId);
  }, []);

  // --- Envoi réel du formulaire --------------------------------------------
  // Si une clé Web3Forms est configurée, la demande part automatiquement.
  // Sinon, repli sur le logiciel de messagerie avec un message pré-rempli :
  // la demande n'est jamais silencieusement perdue.
  const envoyer = async (formName, payload, sujet) => {
    setStatus({ form: formName, state: "sending" });

    const lignes = Object.entries(payload)
      .filter(([k]) => k !== "consent")
      .map(([k, v]) => `${k} : ${v || "—"}`)
      .join("\n");

    if (!estComplet(WEB3FORMS_KEY)) {
      const body = encodeURIComponent(
        `${lignes}\n\n— Envoyé depuis hupsoo-conseil.fr`
      );
      window.location.href =
        `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(sujet)}&body=${body}`;
      setStatus({
        form: formName,
        state: "mailto",
        message:
          "Votre logiciel de messagerie vient de s'ouvrir avec votre demande pré-remplie. " +
          `Il ne reste qu'à l'envoyer. Vous pouvez aussi nous écrire directement à ${CONTACT_EMAIL}.`,
      });
      return;
    }

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: sujet,
          from_name: "Site Hupsoo Conseil",
          ...payload,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus({
          form: formName,
          state: "sent",
          message: "Votre demande est bien arrivée. Nous vous répondons sous 24 h ouvrées.",
        });
      } else {
        throw new Error(data.message || "Envoi refusé");
      }
    } catch (err) {
      setStatus({
        form: formName,
        state: "error",
        message:
          `L'envoi automatique a échoué. Écrivez-nous directement à ${CONTACT_EMAIL} ` +
          `ou appelez le ${CONTACT_PHONE}, nous traiterons votre demande.`,
      });
    }
  };

  const handleDevisSubmit = (e) => {
    e.preventDefault();
    envoyer("devis", { ...formData, demande: "Demande de devis" },
      `[Site] Demande de devis — ${formData.company || formData.name}`);
  };

  const handleRdvSubmit = (e) => {
    e.preventDefault();
    envoyer("rdv", rdvFormData,
      `[Site] ${rdvFormData.objet} — ${rdvFormData.company || rdvFormData.name}`);
  };

  const StatusMessage = ({ form }) => {
    if (!status || status.form !== form) return null;
    if (status.state === "sending") {
      return (
        <p className="form-status sending" role="status">
          <Loader2 size={16} className="spin" /> Envoi en cours…
        </p>
      );
    }
    return (
      <p className={`form-status ${status.state}`} role="status">
        {status.state === "sent" && <CheckCircle size={16} />}
        {status.message}
      </p>
    );
  };

  const faqs = [
    {
      groupe: "Appels d'offres",
      question: "Quels types de marchés publics traitez-vous ?",
      answer: "Nous traitons tous types de marchés publics : travaux, fournitures, services, prestations intellectuelles. Que ce soit pour l'État, les collectivités territoriales ou les établissements publics.",
    },
    {
      groupe: "Appels d'offres",
      question: "Quelle est la durée de traitement d'un dossier ?",
      answer: "La durée dépend de la complexité du dossier. En moyenne, comptez 5 à 10 jours ouvrés pour un dossier complet. Nous nous adaptons toujours aux délais de remise des offres.",
    },
    {
      groupe: "Appels d'offres",
      question: `Qu'est-ce que la certification ${CERTIFICATION_AO.code} ?`,
      answer: `${CERTIFICATION_AO.code} « ${CERTIFICATION_AO.intitule} » est une certification professionnelle enregistrée au ${CERTIFICATION_AO.repertoire} sous le numéro ${CERTIFICATION_AO.numeroFiche} (code NSF ${CERTIFICATION_AO.codeNSF}), par décision du directeur général de France compétences du ${CERTIFICATION_AO.dateEnregistrement}. Elle atteste de la maîtrise du cadre réglementaire de la commande publique, de l'analyse d'un dossier de consultation et de la construction d'une offre recevable. La consultante qui pilote vos réponses en est titulaire depuis le ${CERTIFICATION_AO.dateObtention}, après passage devant un jury. Précision utile : nous détenons cette certification, nous ne la délivrons pas — nous ne sommes pas habilités à la faire passer.`,
    },
    {
      groupe: "Appels d'offres",
      question: "Puis-je changer de formule en cours d'abonnement ?",
      answer: "Oui, vous pouvez évoluer vers une formule supérieure à tout moment. Le changement prend effet dès le mois suivant.",
    },
    {
      groupe: "Appels d'offres",
      question: "Garantissez-vous la réussite de nos candidatures ?",
      answer: "Nous ne pouvons garantir l'attribution d'un marché, mais nous maximisons vos chances grâce à notre expertise et à notre méthode.",
    },
    {
      groupe: "Appels d'offres",
      question: "Comment se déroule l'accompagnement ?",
      answer: "Après un premier échange pour comprendre vos besoins, nous analysons l'appel d'offres, rédigeons le mémoire technique, constituons le dossier administratif et optimisons votre offre financière.",
    },
    ...FAQ_SUBVENTIONS.map((f) => ({ groupe: "Subventions", ...f })),
    ...FAQ_QUALIFICATIONS.map((f) => ({ groupe: "Qualifications", ...f })),
    {
      groupe: "IA sur-mesure",
      question: "Proposez-vous un programme de formation standard ?",
      answer: "Non, et c'est volontaire. Une formation générique à l'IA vous apprend à utiliser des outils ; elle ne change rien à votre organisation. Nous partons de vos process et de vos irritants pour construire un accompagnement qui ne parle que de votre entreprise. Le programme est formalisé et remis avant le démarrage.",
    },
    {
      groupe: "IA sur-mesure",
      question: "Faut-il des connaissances techniques ?",
      answer: "Aucune. Nous nous adressons à des dirigeants, pas à des informaticiens. Si vous savez utiliser une messagerie et que vous connaissez le fonctionnement de votre entreprise, vous avez tout ce qu'il faut.",
    },
    {
      groupe: "IA sur-mesure",
      question: "Est-ce finançable, et qui facture la formation ?",
      answer: `Oui. Nous travaillons avec plusieurs organismes de formation partenaires certifiés Qualiopi et nous vous orientons vers celui qui correspond à votre situation — c'est la condition pour qu'un OPCO puisse prendre votre formation en charge. Les prestations de conseil, de création d'agents IA et d'automatisation sont, elles, réalisées par ${SOCIETE.nomCommercial} — ${SOCIETE.denomination}. Nous montons le dossier de financement avec vous ; la décision de prise en charge appartient toutefois à votre OPCO seul.`,
    },
    {
      groupe: "IA sur-mesure",
      question: "Qu'est-ce que la certification RS7344 ?",
      answer: `RS7344 « ${INTERVENANT.intituleCertification} » est une certification enregistrée au Répertoire spécifique de France Compétences depuis le ${INTERVENANT.dateEnregistrement}. Elle atteste de la capacité à intégrer l'IA dans les processus clés d'une entreprise. L'intervenant qui anime nos accompagnements en est titulaire : c'est le socle méthodologique de nos interventions. Nos formations ne délivrent pas cette certification.`,
    },
    {
      groupe: "IA sur-mesure",
      question: "Les agents et automatisations restent-ils à moi ?",
      answer: "Oui. Tout ce qui est construit pendant l'accompagnement est installé sur vos propres outils et vos propres comptes. Vous en gardez la maîtrise complète, vous pouvez les faire évoluer, et vous n'êtes lié à personne pour continuer à les utiliser.",
    },
    {
      groupe: "IA sur-mesure",
      question: "La formation est-elle accessible en situation de handicap ?",
      answer: `Oui. Nos accompagnements sont accessibles aux personnes en situation de handicap et les modalités s'adaptent au cas par cas. Signalez-nous votre besoin dès le premier échange, à ${CONTACT_EMAIL} ou au ${CONTACT_PHONE} : nous étudions les aménagements avec vous et avec l'organisme partenaire retenu.`,
    },
  ];

  return (
    <div className="app-container">
      {/* ---------------------------------------------------------------- Nav */}
      <nav className={`navbar ${isScrolled ? "scrolled" : ""}`} data-testid="navbar">
        <div className="nav-container">
          <a href="#hero" className="nav-logo" onClick={() => scrollToSection("hero")}>
            <span className="logo-text">Hupsoo</span> <span className="logo-accent">Conseil</span>
          </a>

          <div className={`nav-links ${mobileMenuOpen ? "active" : ""}`}>
            <a href="#hero" onClick={() => scrollToSection("hero")}>Accueil</a>

            <div className="nav-metiers" ref={metiersRef}>
              <button
                type="button"
                className={`nav-metiers-btn ${metiersOpen ? "open" : ""}`}
                aria-expanded={metiersOpen}
                aria-haspopup="true"
                onClick={() => setMetiersOpen((o) => !o)}
              >
                Nos métiers <ChevronDown size={16} />
              </button>
              <div className="nav-metiers-menu" hidden={!metiersOpen}>
                <a href="#qualifications" className="nav-link-new" onClick={() => { setMetiersOpen(false); scrollToSection("qualifications"); }}>
                  Qualifications <span className="nav-badge-new">Nouveau</span>
                </a>
                <a href="#expertise" onClick={() => { setMetiersOpen(false); scrollToSection("expertise"); }}>
                  Réponse aux appels d'offres
                </a>
                <a href="#subventions" onClick={() => { setMetiersOpen(false); scrollToSection("subventions"); }}>
                  Recherche de subventions
                </a>
                <a href="#ia-sur-mesure" onClick={() => { setMetiersOpen(false); scrollToSection("ia-sur-mesure"); }}>
                  Formations &amp; agents IA
                </a>
              </div>
            </div>
            <a href="#offres" onClick={() => scrollToSection("offres")}>Offres</a>
            <a href="#faq" onClick={() => scrollToSection("faq")}>FAQ</a>
            <a href="#contact" onClick={() => scrollToSection("contact")}>Contact</a>
          </div>

          <a href="#contact" className="nav-cta" onClick={() => scrollToSection("contact")}>Prendre RDV</a>

          <button
            className="mobile-menu-btn"
            aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {/* --------------------------------------------------------------- Hero */}
      <section className="hero-section" id="hero" data-testid="hero-section">
        <div className="hero-content">
          <a href="#qualifications" className="hero-announce" onClick={() => scrollToSection("qualifications")}>
            <span className="announce-tag">Nouveau</span>
            Qualibat, RGE, Qualit'EnR, Qualifelec : nous montons le dossier · Diagnostic gratuit
            <ArrowRight size={16} className="announce-arrow" />
          </a>

          <span className="hero-badge">
            <Award size={16} />
            Consultante certifiée {CERTIFICATION_AO.code} — Marchés publics
          </span>
          <h1>La qualification qu'on vous réclame, puis le marché qu'elle ouvre</h1>
          <p className="hero-subtitle">
            Sans le certificat qu'on vous demande, aucun prix ne rattrape le handicap.
          </p>
          <p className="hero-description">
            Dans un environnement de plus en plus concurrentiel, la qualité de votre dossier de
            candidature fait toute la différence. Nous intervenons <strong>partout en France</strong>,
            entièrement à distance : votre implantation n'entre pas en ligne de compte.
          </p>
          <div className="hero-buttons">
            <a href="#contact" className="btn-primary" data-testid="cta-devis" onClick={() => scrollToSection("contact")}>
              Demander un devis gratuit <ArrowRight size={18} />
            </a>
            <a href="#offres" className="btn-secondary" data-testid="cta-decouvrir-offres" onClick={() => scrollToSection("offres")}>
              Découvrir nos offres
            </a>
          </div>
          <div className="hero-stats">
            <div className="stat-card-hero">
              <BadgeCheck size={24} />
              <span className="stat-value">{CERTIFICATION_AO.code}</span>
              <span className="stat-label">Certification France compétences</span>
            </div>
            <div className="stat-card-hero">
              <MapPin size={24} />
              <span className="stat-value">France entière</span>
              <span className="stat-label">Intervention à distance</span>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------- Nos quatre métiers */}
      {/* ORDRE ARBITRÉ LE 18/09/2026 (soir), sur benchmark de concurrence SEO des
          quatre axes — rapports dans docs/benchmark-seo-2026-09-18/ :
          qualifications 4/10, IA 5/10, appels d'offres 6/10, subventions 7,5/10.
          La carte « qualifications » passe donc EN PREMIER, devant les appels
          d'offres : c'est le terrain le moins disputé, 6-12 mois pour la première
          page contre 18-36 sur les marchés publics. Ne pas la remettre en 3e place
          sans refaire le benchmark. La RS7481 reste attachée aux marchés publics
          et ne couvre pas cette offre (ligne rouge n°5 de qualifications.js).
          Récit arbitré le 20/08/2026, mis à jour le 18/09/2026 pour le 4e métier
          (qualifications professionnelles) : les appels d'offres et les subventions
          vont chercher l'argent public ; la qualification n'est pas de l'argent,
          c'est ce qui ouvre l'accès aux marchés et aux aides — elle reste donc
          groupée avec les deux premières, pas isolée dans une carte à part.
          L'IA reste le volet « temps ». Ne pas re-séparer ces trois cartes en
          familles équivalentes : la passerelle entre elles est l'argument
          différenciant. */}
      <section className="bridge-section" data-testid="bridge-section">
        <div className="container">
          <h2>D'abord le certificat.<br />Ensuite les marchés qu'il ouvre.</h2>
          <p className="section-subtitle">
            Hupsoo Conseil accompagne les dirigeants de TPE et PME sur les deux fronts qui décident
            de leur croissance : l'accès aux marchés publics et aux aides — la qualification qui y
            donne droit, les appels d'offres qu'elle rend accessibles et les subventions qui
            financent l'outil — et le temps qu'ils perdent sur des tâches que la technologie sait
            absorber.
          </p>

          <div className="bridge-families">
            <div className="bridge-family">
              <span className="bridge-family-label"><Coins size={16} /> Marchés et aides</span>
              <div className="bridge-family-cards">
                <a href="#qualifications" className="bridge-card" onClick={() => scrollToSection("qualifications")}>
                  <BadgeCheck className="bridge-icon" />
                  <h3>{CARTE_PONT_QUALIF.titre}</h3>
                  <p>{CARTE_PONT_QUALIF.texte}</p>
                  <span className="bridge-link">{CARTE_PONT_QUALIF.lien} <ArrowRight size={16} /></span>
                </a>

                <a href="#expertise" className="bridge-card" onClick={() => scrollToSection("expertise")}>
                  <FileText className="bridge-icon" />
                  <h3>Réponse aux appels d'offres</h3>
                  <p>
                    Veille, analyse du cahier des charges, mémoire technique, pièces administratives,
                    positionnement financier. De la détection de la consultation jusqu'au dépôt du pli.
                  </p>
                  <span className="bridge-link">Découvrir notre accompagnement <ArrowRight size={16} /></span>
                </a>

                <a href="#subventions" className="bridge-card" onClick={() => scrollToSection("subventions")}>
                  <Banknote className="bridge-icon" />
                  <h3>Recherche de subventions</h3>
                  <p>
                    Département, région, État, Europe, opérateurs sectoriels. On identifie les
                    dispositifs mobilisables sur vos projets, on monte les dossiers, on suit jusqu'au
                    versement.
                  </p>
                  <span className="bridge-link">Découvrir notre accompagnement <ArrowRight size={16} /></span>
                </a>

              </div>
              <p className="bridge-family-note">
                La qualification est réclamée dès la consultation : sans elle, le dossier part
                déjà perdant. Une fois le marché gagné, il faut financer l'outil qui permet de
                l'exécuter. Les trois se préparent ensemble, dans cet ordre.
              </p>
            </div>

            <div className="bridge-family">
              <span className="bridge-family-label"><Zap size={16} /> Le temps</span>
              <div className="bridge-family-cards">
                <a href="#ia-sur-mesure" className="bridge-card accent" onClick={() => scrollToSection("ia-sur-mesure")}>
                  <Bot className="bridge-icon" />
                  <h3>IA sur-mesure</h3>
                  <p>
                    Formations personnalisées, agents IA construits pour votre métier, automatisations
                    branchées sur vos outils. Conçu à partir de vos besoins, pas d'un catalogue.
                  </p>
                  <span className="bridge-link">Découvrir notre approche <ArrowRight size={16} /></span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------- Réassurance */}
      <div className="trust-bar" data-testid="trust-bar">
        <div className="container trust-items">
          <span><BadgeCheck size={18} /> Consultante certifiée {CERTIFICATION_AO.code} — Marchés publics</span>
          <span><BadgeCheck size={18} /> Consultant certifié RS7344 — IA</span>
          <span><MapPin size={18} /> Partout en France, à distance</span>
          <span><Award size={18} /> Organismes partenaires certifiés Qualiopi</span>
          <span><Euro size={18} /> Financement OPCO possible</span>
          <span><Zap size={18} /> Réponse sous 24 h ouvrées</span>
        </div>
      </div>

      {/* ---------------------------------------------------------- Expertise */}
      <section className="expertise-section" id="expertise" data-testid="expertise-section">
        <div className="container">
          <h2>Notre Expertise</h2>
          <p className="section-subtitle">Une compétence certifiée par l'État, mise au service de chaque dossier</p>

          {/* Bloc certification RS7481 — mention texte, le certificat n'est pas publié */}
          <div className="certif-card" data-testid="certif-rs7481">
            <div className="certif-seal" aria-hidden="true"><BadgeCheck size={40} /></div>
            <div className="certif-body">
              <span className="certif-overline">Certification professionnelle</span>
              <h3>{CERTIFICATION_AO.code} — « {CERTIFICATION_AO.intitule} »</h3>
              <p>
                Nos réponses aux appels d'offres sont pilotées par une consultante titulaire de la
                certification {CERTIFICATION_AO.code}, obtenue le {CERTIFICATION_AO.dateObtention}.
                Ce n'est pas un label acheté : c'est un titre passé devant un jury, qui atteste de la
                maîtrise du cadre réglementaire de la commande publique, de l'analyse d'un DCE et de
                la construction d'une offre recevable.
              </p>
              <ul className="certif-meta">
                <li><strong>Répertoire</strong> {CERTIFICATION_AO.repertoire}, fiche n° {CERTIFICATION_AO.numeroFiche}</li>
                <li><strong>Code NSF</strong> {CERTIFICATION_AO.codeNSF}</li>
                <li><strong>Certificateur</strong> {CERTIFICATION_AO.certificateur}</li>
                <li><strong>Enregistrement</strong> décision du directeur général de France compétences du {CERTIFICATION_AO.dateEnregistrement}</li>
              </ul>
              <p className="certif-note">
                Certification détenue par notre consultante à titre personnel.
                {" "}{SOCIETE.nomCommercial} ne la délivre pas et n'est pas habilité à la faire passer.
                {" "}
                <a href={CERTIFICATION_AO.urlFiche} target="_blank" rel="noopener noreferrer">
                  Vérifier la fiche sur France compétences <ArrowRight size={14} />
                </a>
              </p>
            </div>
          </div>

          <div className="expertise-grid">
            <div className="expertise-card">
              <img src="/images/expertise-5668859.jpg" alt="Consultante travaillant sur un dossier de candidature" loading="lazy" />
              <h3>Une méthode adossée à un référentiel</h3>
              <p>Analyse du DCE, sélection des critères qui pèsent, construction d'une offre recevable : nous appliquons le référentiel de la certification {CERTIFICATION_AO.code}, pas une recette maison. Vous savez sur quoi repose notre travail.</p>
            </div>
            <div className="expertise-card">
              <img src="/images/expertise-5668479.jpg" alt="Échange entre un consultant et un dirigeant d'entreprise" loading="lazy" />
              <h3>Accompagnement sur mesure, tous secteurs confondus</h3>
              <p>Que vous soyez dans le bâtiment, l'informatique, le nettoyage, la sécurité, le conseil ou tout autre domaine, nos consultants maîtrisent les codes des marchés publics et privés.</p>
            </div>
            <div className="expertise-card">
              <img src="/images/expertise-12902872.jpg" alt="Dossier administratif en cours de constitution" loading="lazy" />
              <h3>De la veille à la remise de l'offre</h3>
              <p>Analyse du cahier des charges, rédaction du mémoire technique, constitution des pièces administratives... Nous vous accompagnons à chaque étape.</p>
            </div>
          </div>

          <div className="services-grid">
            <h3 className="services-title">De la veille à la remise de l'offre, nous gérons tout</h3>
            <div className="services-cards">
              <div className="service-card">
                <div className="service-icon-wrapper"><FileText className="service-icon" /></div>
                <h4>Analyse du cahier des charges</h4>
                <p>Décryptage complet de vos appels d'offres</p>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper"><Target className="service-icon" /></div>
                <h4>Rédaction du mémoire technique</h4>
                <p>Valorisation de votre expertise et savoir-faire</p>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper"><Users className="service-icon" /></div>
                <h4>Constitution des pièces</h4>
                <p>Dossier administratif complet et conforme</p>
              </div>
              <div className="service-card">
                <div className="service-icon-wrapper"><TrendingUp className="service-icon" /></div>
                <h4>Optimisation financière</h4>
                <p>Positionnement stratégique de votre offre</p>
              </div>
            </div>
          </div>

          {/* Guides appels d'offres — ajoutés le 18/09/2026.
              memoire-technique (2 931 mots, 3e page du site) et
              externaliser-reponse-appel-offres n'avaient AUCUN lien dans le
              site rendu : seulement la colonne du pied de page. Même défaut
              que les six guides qualifications, en place depuis plus longtemps.
              Réutilise la grille .qualif-guides. */}
          <h3 className="expertise-guides-titre">Nos guides sur les appels d'offres</h3>
          <div className="qualif-guides">
            <a className="qualif-guide" href="/memoire-technique">
              <h4>Le mémoire technique, de A à Z</h4>
              <p>
                Dix leviers concrets, et pour chacun l'erreur qui coûte le marché. Le document
                qui pèse le plus lourd dans la note, et celui qu'on rédige le plus vite.
              </p>
              <span className="qualif-guide-lien">Lire le guide <ArrowRight size={15} /></span>
            </a>
            <a className="qualif-guide" href="/positionnement-prix-appel-offres">
              <h4>Pourquoi le meilleur dossier ne gagne pas toujours</h4>
              <p>
                Deux marchés perdus, racontés sans fard : un excellent mémoire battu par un
                industriel, et un chiffrage trop haut qui coûte la place avant la première
                ligne écrite.
              </p>
              <span className="qualif-guide-lien">Lire le guide <ArrowRight size={15} /></span>
            </a>
            <a className="qualif-guide" href="/externaliser-reponse-appel-offres">
              <h4>Externaliser ses réponses : ce que ça change</h4>
              <p>
                Ce qui reste chez vous, ce qui part chez nous, et à partir de quel volume de
                consultations l'externalisation devient rentable.
              </p>
              <span className="qualif-guide-lien">Lire le guide <ArrowRight size={15} /></span>
            </a>
          </div>

          <div className="sectors-section">
            <h3>Tous secteurs d'activité confondus</h3>
            {/* Chaque secteur pointe vers sa page dédiée : ces liens sont le seul
                maillage de l'accueil vers les pages sectorielles. */}
            <div className="sectors-grid">
              <a className="sector-item" href="/appels-offres-btp"><Building2 /> Bâtiment &amp; Travaux Publics</a>
              <a className="sector-item" href="/appels-offres-informatique"><Monitor /> Informatique &amp; Numérique</a>
              <a className="sector-item" href="/appels-offres-nettoyage"><Sparkles /> Nettoyage &amp; Services</a>
              <a className="sector-item" href="/appels-offres-securite"><Shield /> Sécurité &amp; Surveillance</a>
              <a className="sector-item" href="/appels-offres-conseil-formation"><GraduationCap /> Conseil &amp; Formation</a>
              <a className="sector-item" href="/appels-offres-fournitures"><Package /> Fournitures &amp; Équipements</a>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------- Recherche de subventions */}
      {/* Contenu dans src/data/subventions.js — lignes rouges en tête de ce fichier :
          aucun chiffre de performance, aucune garantie d'obtention, la RS7481 ne
          couvre pas cette offre, et les CEE sont un dispositif cité, pas un rôle. */}
      <section className="subv-section" id="subventions" data-testid="subventions-section">
        <div className="container">
          <span className="subv-overline">Nouveau · Recherche de subventions</span>
          <h2>Les aides existent.<br />Encore faut-il savoir qu'elles vous concernent.</h2>
          <p className="subv-lede">{SUBVENTIONS_ACCROCHE}</p>

          {/* Le déclic : la règle d'antériorité */}
          <div className="subv-alerte">
            <AlertTriangle className="subv-alerte-icon" />
            <div>
              <h3>{REGLE_ANTERIORITE.titre}</h3>
              <p>{REGLE_ANTERIORITE.texte}</p>
            </div>
          </div>

          {/* Les quatre niveaux */}
          <h3 className="subv-subtitle">Quatre niveaux de financement, rarement explorés en entier</h3>
          <div className="subv-niveaux">
            {NIVEAUX.map((n) => {
              const Icone = ICONES_SUBVENTIONS[n.icon];
              return (
                <div className="subv-niveau" key={n.titre}>
                  <Icone className="subv-niveau-icon" />
                  <h4>{n.titre}</h4>
                  <p>{n.texte}</p>
                </div>
              );
            })}
          </div>

          {/* Les opérateurs sectoriels */}
          <div className="subv-operateurs">
            <h3>Et les opérateurs auxquels personne ne pense</h3>
            <p className="subv-operateurs-intro">{OPERATEURS_INTRO}</p>
            <div className="subv-operateurs-liste">
              {OPERATEURS.map((o) => (
                <span key={o}><CheckCircle size={16} /> {o}</span>
              ))}
            </div>
          </div>

          {/* La méthode */}
          <h3 className="subv-subtitle">Comment nous procédons</h3>
          <div className="subv-method">
            {ETAPES_SUBVENTIONS.map((e) => {
              const Icone = ICONES_SUBVENTIONS[e.icon];
              return (
                <div className="subv-step" key={e.n}>
                  <span className="subv-step-num">{e.n}</span>
                  <div className="subv-step-body">
                    <h4><Icone size={22} /> {e.titre}</h4>
                    <p>{e.texte}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Les engagements — réassurance en l'absence de chiffres */}
          <div className="subv-engagements">
            <h3>Nos engagements</h3>
            <ul>
              {ENGAGEMENTS.map((e) => (
                <li key={e}><CheckCircle /> {e}</li>
              ))}
            </ul>
          </div>

          {/* Réserves */}
          <div className="subv-reserves">
            <h3><Shield size={20} /> Ce que nous ne promettons pas</h3>
            <ul>
              {RESERVES.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>

          <div className="subv-cta">
            <p>Le diagnostic est gratuit et sans engagement.</p>
            <a href="#contact" className="btn-primary" onClick={() => scrollToSection("contact", "Recherche de subventions")}>
              Vérifier gratuitement à quelles aides vous avez droit <ArrowRight size={18} />
            </a>
            <a href="/recherche-subventions-entreprise" className="subv-cta-link">
              Comprendre comment se monte un dossier de subvention <ArrowRight size={14} />
            </a>
            <a href="/aides-subventions-occitanie" className="subv-cta-link">
              Les aides mobilisables en Occitanie <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ------------------------------------ Qualifications professionnelles */}
      {/* Contenu dans src/data/qualifications.js — lignes rouges en tête de ce
          fichier : aucun chiffre de performance, aucune garantie d'obtention,
          aucun logo d'organisme, et Hupsoo ne délivre aucune qualification. */}
      <section className="qualif-section" id="qualifications" data-testid="qualifications-section">
        <div className="container">
          <span className="qualif-overline">Nouveau · Qualifications professionnelles</span>
          <h2>{TITRE_QUALIF.ligne1}<br />{TITRE_QUALIF.ligne2}</h2>
          <p className="qualif-lede">{QUALIF_ACCROCHE}</p>

          {/* Le constat */}
          <div className="qualif-constat">
            <HardHat className="qualif-constat-icon" />
            <div>
              <h3>{CONSTAT.titre}</h3>
              <p>{CONSTAT.texte}</p>
            </div>
          </div>

          {/* Les quatre familles */}
          <h3 className="qualif-subtitle">Les qualifications que nous montons</h3>
          <div className="qualif-familles">
            {FAMILLES.map((f) => {
              const Icone = ICONES_QUALIFICATIONS[f.icon];
              return (
                <div className="qualif-famille" key={f.nom}>
                  <Icone className="qualif-famille-icon" />
                  <h4>{f.nom}</h4>
                  <p className="qualif-famille-pourqui">{f.pourQui}</p>
                  <p>{f.ceQuOuvre}</p>
                </div>
              );
            })}
          </div>

          {/* Le vrai frein */}
          <div className="qualif-frein">
            <h3>{FREIN.titre}</h3>
            <p>{FREIN.texte}</p>
            <ul className="qualif-frein-liste">
              {FREIN.pieces.map((p) => (
                <li key={p}><CheckCircle size={16} /> {p}</li>
              ))}
            </ul>
          </div>

          {/* La méthode */}
          <h3 className="qualif-subtitle">Comment nous procédons</h3>
          <div className="qualif-method">
            {ETAPES_QUALIF.map((e) => {
              const Icone = ICONES_QUALIFICATIONS[e.icon];
              return (
                <div className="qualif-step" key={e.n}>
                  <span className="qualif-step-num">{e.n}</span>
                  <div className="qualif-step-body">
                    <h4><Icone size={22} /> {e.titre}</h4>
                    <p>{e.texte}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Les engagements */}
          <div className="qualif-engagements">
            <h3>Nos engagements</h3>
            <ul>
              {ENGAGEMENTS_QUALIF.map((e) => (
                <li key={e}><CheckCircle /> {e}</li>
              ))}
            </ul>
          </div>

          {/* Les réserves */}
          <div className="qualif-reserves">
            <h3><Shield size={20} /> Ce que nous ne promettons pas</h3>
            <ul>
              {RESERVES_QUALIF.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>

          {/* Les guides — ajoutés le 18/09/2026.
              Ces six pages n'avaient AUCUN lien dans le site rendu : elles ne
              figuraient que dans le <noscript> de l'accueil, donc lisibles de
              Google seul. Ne pas les y renvoyer. */}
          <h3 className="qualif-subtitle">Les questions qu'on nous pose</h3>
          <div className="qualif-guides">
            {GUIDES.map((g) => (
              <a className="qualif-guide" href={g.href} key={g.href}>
                <h4>{g.titre}</h4>
                <p>{g.texte}</p>
                <span className="qualif-guide-lien">Lire le guide <ArrowRight size={15} /></span>
              </a>
            ))}
          </div>

          <div className="qualif-cta">
            <p>Le diagnostic d'éligibilité est gratuit et sans engagement.</p>
            <a href="#contact" className="btn-primary" onClick={() => scrollToSection("contact", "Qualification professionnelle")}>
              {OFFRE_QUALIF_CONTENU.cta} <ArrowRight size={18} />
            </a>
            <a href="/accompagnement-qualification-qualibat-rge" className="qualif-cta-link">
              Comment se monte un dossier de qualification <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ IA sur-mesure */}
      <section className="ia-section" id="ia-sur-mesure" data-testid="ia-section">
        <div className="container">
          <span className="ia-overline">Nouveau · Formations &amp; agents IA sur-mesure</span>
          <h2>L'IA appliquée à votre entreprise.<br />Pas à l'entreprise en général.</h2>
          <p className="ia-lede">
            La plupart des formations à l'IA vous apprennent à utiliser des outils. Nous faisons
            l'inverse : nous partons de vos process, de vos documents et de vos irritants, et nous
            construisons avec vous ce qui vous fera gagner du temps dès la semaine suivante.
            Rien n'est générique — ni le programme, ni les agents, ni les automatisations.
          </p>

          <div className="ia-badges">
            <span className="ia-badge"><Award size={18} /> Formations réalisées avec des organismes partenaires certifiés Qualiopi</span>
            <span className="ia-badge"><BadgeCheck size={18} /> Consultant certifié RS7344 — « {INTERVENANT.intituleCertification} »</span>
          </div>
          <p className="ia-legal">
            Les actions de formation sont réalisées avec des organismes de formation partenaires
            certifiés Qualiopi. Nous vous orientons vers celui dont le référencement correspond à
            votre situation et à votre financeur.
          </p>

          {/* Trois piliers */}
          <div className="ia-pillars">
            <div className="ia-pillar">
              <GraduationCap className="ia-pillar-icon" />
              <h3>Des formations personnalisées</h3>
              <p>
                Nous ne déroulons pas un programme. Nous partons de votre métier, de votre équipe et
                de votre niveau de départ pour bâtir une formation qui ne parle que de votre
                entreprise. Un dirigeant seul, un comité de direction ou une équipe complète : le
                format s'adapte, jamais l'inverse. Le déroulé complet est détaillé sur notre page
                {" "}<a href="/formation-ia-dirigeant">formation IA pour dirigeants de TPE et PME</a>.
              </p>
            </div>
            <div className="ia-pillar">
              <Bot className="ia-pillar-icon" />
              <h3>Des agents IA qui vous appartiennent</h3>
              <p>
                Des assistants entraînés sur vos propres documents, votre vocabulaire et vos
                procédures. Un agent qui rédige comme vous, un autre qui répond à vos clients, un
                troisième qui prépare vos dossiers. Ils sont construits pour vous, ils restent chez vous.
              </p>
            </div>
            <div className="ia-pillar">
              <Workflow className="ia-pillar-icon" />
              <h3>Des automatisations sur mesure</h3>
              <p>
                Vos outils cessent de vivre chacun dans leur coin. Nous connectons ce que vous
                utilisez déjà pour que les tâches répétitives se fassent sans vous : relances,
                saisies, suivis, veille, reporting. Une fois en place, ça tourne tout seul.
              </p>
            </div>
          </div>

          {/* Méthode */}
          <h3 className="ia-subtitle">Comment nous travaillons</h3>
          <div className="ia-method">
            {[
              { n: "01", icon: <MessageSquare size={22} />, t: "On écoute", d: "Un premier échange pour comprendre votre activité, vos process et ce qui vous coûte le plus de temps. Sans engagement." },
              { n: "02", icon: <Search size={22} />, t: "On cible", d: "Nous identifions les chantiers où l'IA produit un effet réel et rapide chez vous, et nous écartons sans hésiter ceux qui n'en valent pas la peine. Vous recevez une proposition claire et chiffrée." },
              { n: "03", icon: <ListChecks size={22} />, t: "On construit ensemble", d: "Formation et mise en œuvre avancent en même temps. Vous ne regardez pas une démonstration : vos agents et vos automatisations se construisent pendant l'accompagnement, sur vos vraies données." },
              { n: "04", icon: <Rocket size={22} />, t: "On vous laisse autonome", d: "À la fin, tout fonctionne et tout vous appartient. Vous savez faire évoluer ce qui a été mis en place, et nous restons joignables si besoin." },
            ].map((e) => (
              <div className="ia-step" key={e.n}>
                <span className="ia-step-num">{e.n}</span>
                <div className="ia-step-body">
                  <h4>{e.icon} {e.t}</h4>
                  <p>{e.d}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Exemples */}
          <h3 className="ia-subtitle">Quelques exemples de ce que nous mettons en place</h3>
          <div className="ia-examples">
            <span><Inbox size={18} /> Un assistant qui trie et résume les demandes entrantes</span>
            <span><PenLine size={18} /> Un agent qui rédige vos documents commerciaux dans votre style</span>
            <span><MessageSquare size={18} /> Un agent qui répond aux questions récurrentes de vos clients</span>
            <span><Repeat size={18} /> Des relances et un CRM qui se mettent à jour sans vous</span>
            <span><Search size={18} /> Une veille automatique sur votre marché et votre réglementation</span>
            <span><BarChart3 size={18} /> Un reporting qui arrive seul dans votre boîte chaque lundi</span>
          </div>
          <p className="ia-note">
            Cette liste n'est pas un catalogue. C'est ce que d'autres dirigeants nous ont demandé.
            Le vôtre sera différent.
          </p>

          {/* Fiche formation — indicateur 1 Qualiopi */}
          <div className="ia-fiche">
            <h3>Nos formations en pratique</h3>
            <p className="ia-fiche-intro">
              Nos formations étant construites sur mesure, le contenu, la durée et le tarif sont
              définis après analyse de votre besoin. Voici le cadre commun à toutes nos interventions.
            </p>
            <dl className="ia-fiche-grid">
              <div><dt>Public visé</dt><dd>Dirigeants, directeurs généraux, gérants et cadres de TPE et PME, ainsi que leurs collaborateurs impliqués dans les processus concernés.</dd></div>
              <div><dt>Prérequis</dt><dd>Aucun prérequis technique. Une pratique courante des outils bureautiques et une connaissance de ses propres processus suffisent.</dd></div>
              <div><dt>Objectifs</dt><dd>Identifier les opportunités d'intégration de l'IA dans ses processus clés · construire un plan d'intégration adapté à sa structure · mettre en œuvre des solutions d'IA et d'automatisation · accompagner le déploiement auprès de ses équipes · évaluer l'impact sur la performance de l'entreprise.</dd></div>
              <div><dt>Contenu et durée</dt><dd>Définis sur mesure après un entretien d'analyse du besoin, et formalisés dans un programme personnalisé remis avant le démarrage.</dd></div>
              <div><dt>Format</dt><dd>Intra-entreprise, dans vos locaux ou à distance. Individuel ou en petit groupe.</dd></div>
              <div><dt>Méthodes mobilisées</dt><dd>Alternance d'apports et d'ateliers pratiques sur vos propres cas. Les livrables sont produits pendant la formation. Support et compte rendu remis à chaque participant.</dd></div>
              <div><dt>Modalités d'évaluation</dt><dd>Questionnaire de positionnement en amont, évaluation continue par la production des livrables en atelier, évaluation des acquis en fin de parcours et questionnaire de satisfaction.</dd></div>
              <div><dt>Délais d'accès</dt><dd>Démarrage possible sous 15 jours ouvrés après validation du programme. Délai porté à un mois environ en cas de demande de prise en charge par un OPCO.</dd></div>
              <div><dt>Tarif</dt><dd>Sur devis, établi après l'entretien d'analyse du besoin. Le devis détaille la durée, le contenu et les livrables.</dd></div>
              <div><dt>Accessibilité</dt><dd>Nos accompagnements sont accessibles aux personnes en situation de handicap. Signalez-nous votre besoin dès le premier échange : nous étudions les adaptations avec vous et avec l'organisme partenaire retenu.</dd></div>
              <div><dt>Contact</dt><dd>{CONTACT_EMAIL} — {CONTACT_PHONE}</dd></div>
            </dl>
          </div>

          {/* Financement OPCO */}
          <div className="ia-opco">
            <h3><Euro size={22} /> Votre OPCO peut financer votre formation</h3>
            <p>
              Nous travaillons avec plusieurs organismes de formation partenaires certifiés Qualiopi.
              Votre formation est portée par celui qui correspond à votre situation, ce qui lui permet
              de faire l'objet d'une demande de prise en charge auprès de votre OPCO — l'opérateur de
              compétences dont dépend votre entreprise.
            </p>
            <p>
              Le montant dépend de votre branche, de votre effectif et des dispositifs en cours. Nous
              nous occupons du dossier avec vous : convention de formation, programme personnalisé,
              devis et pièces justificatives.
            </p>
            <div className="ia-opco-steps">
              <div><span>1</span><strong>Vous nous contactez</strong><p>Nous identifions votre OPCO, le budget mobilisable et l'organisme partenaire adapté.</p></div>
              <div><span>2</span><strong>Nous montons le dossier</strong><p>Convention, programme et devis conformes aux attendus du financeur.</p></div>
              <div><span>3</span><strong>Vous déposez la demande</strong><p>Nous restons à vos côtés jusqu'à la réponse.</p></div>
            </div>
            <p className="ia-opco-mention">
              L'accord de prise en charge relève de la seule décision de votre OPCO.
              {" "}{SOCIETE.nomCommercial} ne peut en garantir l'obtention.
            </p>
          </div>

          {/* Qui vous accompagne */}
          <div className="ia-author">
            <div className="ia-author-photo" aria-hidden="true"><Users size={48} /></div>
            <div className="ia-author-body">
              <h3>Un praticien, pas un théoricien</h3>
              <p>
                L'intervenant qui anime nos accompagnements dirige Hupsoo Conseil et travaille au
                quotidien sur des réponses à appels d'offres pour des TPE et PME de tous secteurs.
                Il est certifié RS7344 « {INTERVENANT.intituleCertification} » — certification
                enregistrée au Répertoire spécifique de France Compétences le
                {" "}{INTERVENANT.dateEnregistrement}. Les agents et les automatisations qu'il
                installe aujourd'hui chez ses clients, il les a d'abord déployés dans son propre
                cabinet.
              </p>
              <p>
                C'est toute la différence : vous ne repartez pas avec des principes, vous repartez
                avec ce qui tourne déjà chez nous.
              </p>
            </div>
          </div>

          <div className="ia-cta">
            <p>Le premier échange est gratuit et sans engagement.</p>
            <div className="ia-cta-buttons">
              <a href="#contact" className="btn-primary" onClick={() => scrollToSection("contact", "Formation IA / agents sur-mesure")}>
                Prendre un rendez-vous découverte <ArrowRight size={18} />
              </a>
              <a href="#contact" className="btn-secondary" onClick={() => scrollToSection("contact", "Financement OPCO")}>
                Vérifier mon financement OPCO
              </a>
            </div>
            <div className="ia-cta-links">
              <a href="/creer-agent-ia">Comment se crée un agent IA <ArrowRight size={14} /></a>
              <a href="/agents-ia-entreprise">Ce qui s'automatise vraiment dans une PME <ArrowRight size={14} /></a>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- Offres */}
      <section className="offres-section" id="offres" data-testid="offres-section">
        <div className="container">
          <span className="section-badge">Nos Offres</span>
          <h2>Des formules adaptées à vos besoins</h2>
          <p className="section-subtitle">
            {offreTab === "ao"
              ? "Choisissez l'accompagnement qui correspond à votre activité sur les marchés publics"
              : offreTab === "subv"
              ? "Le cadrage est facturé, le diagnostic ne l'est pas : vous savez ce qu'il y a à aller chercher avant d'engager quoi que ce soit"
              : offreTab === "qualif"
              ? "Un forfait par dossier, annoncé avant de commencer, demandes de compléments comprises"
              : "Chaque accompagnement IA est construit à partir de vos besoins — il n'y a donc pas de formule standard"}
          </p>

          <div className="offres-tabs" role="tablist" aria-label="Type d'offre">
            <button role="tab" aria-selected={offreTab === "ao"} className={offreTab === "ao" ? "active" : ""} onClick={() => setOffreTab("ao")}>
              Appels d'offres
            </button>
            <button role="tab" aria-selected={offreTab === "subv"} className={offreTab === "subv" ? "active" : ""} onClick={() => setOffreTab("subv")}>
              Subventions
            </button>
            <button role="tab" aria-selected={offreTab === "qualif"} className={offreTab === "qualif" ? "active" : ""} onClick={() => setOffreTab("qualif")}>
              Qualifications
            </button>
            <button role="tab" aria-selected={offreTab === "ia"} className={offreTab === "ia" ? "active" : ""} onClick={() => setOffreTab("ia")}>
              IA sur-mesure
            </button>
          </div>

          {offreTab === "ao" ? (
            <div className="pricing-grid fade-in">

              <div className="pricing-card">
                <h3>Dossier Unique</h3>
                <div className="price">1 000 € - 2 500 € HT<span className="price-period">/ dossier</span></div>
                <p className="price-success-fee" data-testid="success-fee-dossier-unique">+ un pourcentage à la réussite</p>
                <p className="price-description">Traitement ponctuel d'un appel d'offres</p>
                <ul className="features-list">
                  <li><CheckCircle /> 1 dossier complet</li>
                  <li><CheckCircle /> Analyse approfondie du cahier des charges</li>
                  <li><CheckCircle /> Rédaction du mémoire technique</li>
                  <li><CheckCircle /> Constitution des pièces administratives</li>
                  <li><CheckCircle /> Optimisation de l'offre financière</li>
                  <li><CheckCircle /> Support dédié jusqu'à la remise</li>
                </ul>
                <a href="#contact" className="btn-outline" onClick={() => scrollToSection("contact", "Réponse à un appel d'offres")}>Commander un dossier</a>
              </div>

              <div className="pricing-card popular">
                <span className="popular-badge"><Star size={14} /> Plus Populaire</span>
                <h3>Essentiel</h3>
                <div className="price">2 000 € - 2 500 € HT<span className="price-period">/ mois</span></div>
                <p className="price-success-fee" data-testid="success-fee-essentiel">+ un pourcentage à la réussite</p>
                <p className="price-description">Pour les entreprises démarrant dans les marchés publics</p>
                <ul className="features-list">
                  <li><CheckCircle /> Jusqu'à 2 dossiers par mois</li>
                  <li><CheckCircle /> Veille personnalisée des appels d'offres</li>
                  <li><CheckCircle /> Rédaction complète des réponses</li>
                  <li><CheckCircle /> Optimisation de l'offre financière</li>
                  <li><CheckCircle /> Support prioritaire par téléphone</li>
                  <li><CheckCircle /> Relecture et validation</li>
                </ul>
                <a href="#contact" className="btn-primary" onClick={() => scrollToSection("contact", "Réponse à un appel d'offres")}>Choisir Essentiel</a>
              </div>

              <div className="pricing-card">
                <h3>Premium</h3>
                <div className="price">2 500 € - 3 000 € HT<span className="price-period">/ mois</span></div>
                <p className="price-success-fee" data-testid="success-fee-premium">+ un pourcentage à la réussite</p>
                <p className="price-description">Pour les entreprises actives sur les marchés publics</p>
                <ul className="features-list">
                  <li><CheckCircle /> Dossiers illimités</li>
                  <li><CheckCircle /> Veille personnalisée avancée</li>
                  <li><CheckCircle /> Rédaction et optimisation complète</li>
                  <li><CheckCircle /> Accompagnement stratégique</li>
                  <li><CheckCircle /> Support dédié 24/7</li>
                  <li><CheckCircle /> Formation aux marchés publics</li>
                  <li><CheckCircle /> Analyse de la concurrence</li>
                </ul>
                <a href="#contact" className="btn-outline" onClick={() => scrollToSection("contact", "Réponse à un appel d'offres")}>Choisir Premium</a>
              </div>

              <div className="pricing-card">
                <h3>Sur-mesure</h3>
                <div className="price">Sur devis</div>
                <p className="price-success-fee" data-testid="success-fee-sur-mesure">+ un pourcentage à la réussite</p>
                <p className="price-description">Solution personnalisée pour vos besoins spécifiques</p>
                <ul className="features-list">
                  <li><CheckCircle /> Volume adapté à vos besoins</li>
                  <li><CheckCircle /> Services personnalisés</li>
                  <li><CheckCircle /> Accompagnement dédié</li>
                  <li><CheckCircle /> Formations sur mesure</li>
                  <li><CheckCircle /> Conseil stratégique approfondi</li>
                  <li><CheckCircle /> Reporting détaillé</li>
                </ul>
                <a href="#contact" className="btn-outline" onClick={() => scrollToSection("contact", "Plusieurs sujets / Autre")}>Nous contacter</a>
              </div>
            </div>
          ) : offreTab === "subv" ? (
            <div className="subv-offer fade-in">
              <div className="pricing-card popular subv-offer-card">
                <h3>{OFFRE_SUBV_CONTENU.titre}</h3>
                <div className="price">{OFFRE_SUBVENTIONS.prix}<span className="price-period">{OFFRE_SUBVENTIONS.periode}</span></div>
                <p className="price-success-fee" data-testid="success-fee-subventions">{OFFRE_SUBVENTIONS.succes}</p>
                <p className="price-description">{OFFRE_SUBV_CONTENU.description}</p>
                <ul className="features-list">
                  {OFFRE_SUBV_CONTENU.features.map((f) => (
                    <li key={f}><CheckCircle /> {f}</li>
                  ))}
                </ul>
                <a href="#contact" className="btn-primary" onClick={() => scrollToSection("contact", "Recherche de subventions")}>
                  {OFFRE_SUBV_CONTENU.cta}
                </a>
              </div>
              <div className="subv-offer-aside">
                <h4>Comment se décompose le tarif</h4>
                <p>
                  <strong>Le diagnostic est gratuit</strong> : 45 minutes pour savoir s'il y a
                  quelque chose à aller chercher. Si la réponse est non, vous ne payez rien et nous
                  vous le disons franchement.
                </p>
                <p>
                  <strong>Le cadrage est facturé au forfait</strong> : c'est le travail de
                  cartographie des dispositifs, de vérification des critères et de calendrier de
                  dépôt. Il vous appartient, même si vous décidez ensuite de monter les dossiers
                  vous-même.
                </p>
                <p>
                  <strong>Le montage des dossiers</strong> s'y ajoute avec un pourcentage à la
                  réussite, défini avec vous avant de commencer et calculé sur les aides
                  effectivement obtenues.
                </p>
                <p className="subv-offer-note">
                  Nos honoraires ne sont pas une dépense subventionnable dans la plupart des
                  dispositifs : ils restent à votre charge.
                </p>
              </div>
            </div>
          ) : offreTab === "qualif" ? (
            <div className="qualif-offer fade-in">
              <div className="pricing-card popular qualif-offer-card">
                <span className="popular-badge"><Star size={14} /> Nouveau</span>
                <h3>{OFFRE_QUALIF_CONTENU.titre}</h3>
                <div className="price">{OFFRE_QUALIFICATIONS.premiere}<span className="price-period">/ première demande</span></div>
                <p className="price-description">{OFFRE_QUALIF_CONTENU.description}</p>
                <ul className="features-list">
                  {OFFRE_QUALIF_CONTENU.features.map((f) => (
                    <li key={f}><CheckCircle /> {f}</li>
                  ))}
                </ul>
                <a href="#contact" className="btn-primary" onClick={() => scrollToSection("contact", "Qualification professionnelle")}>
                  {OFFRE_QUALIF_CONTENU.cta}
                </a>
              </div>
              <div className="qualif-offer-aside">
                <h4>{CAS_DE_FIGURE_TITRE}</h4>
                <dl className="qualif-offer-grille">
                  {CAS_DE_FIGURE.map((c) => (
                    <Fragment key={c.cas}>
                      <dt>{c.cas}</dt>
                      <dd>{OFFRE_QUALIFICATIONS[c.montantCle]} — {c.texte}</dd>
                    </Fragment>
                  ))}
                </dl>
                <p className="qualif-offer-note">
                  {OFFRE_QUALIFICATIONS.mention} Pour une certification de système, l'audit est
                  facturé séparément par l'organisme accrédité qui la délivre.
                </p>
              </div>
            </div>
          ) : (
            <div className="ia-offer fade-in">
              <div className="ia-offer-head">
                <h3>Un accompagnement construit pour vous</h3>
                <div className="price">Sur devis</div>
              </div>
              <p className="ia-offer-text">
                Parce qu'aucune entreprise n'a les mêmes process, nous ne proposons pas de formule
                standard. Le périmètre, la durée et le tarif sont définis ensemble après un premier
                échange — gratuit et sans engagement.
              </p>
              <ul className="features-list ia-offer-list">
                <li><CheckCircle /> Analyse de vos process et identification des chantiers prioritaires</li>
                <li><CheckCircle /> Formation personnalisée de vous et de vos équipes</li>
                <li><CheckCircle /> Conception et mise en service de vos agents IA</li>
                <li><CheckCircle /> Mise en place de vos automatisations</li>
                <li><CheckCircle /> Transfert d'autonomie et documentation</li>
                <li><CheckCircle /> Montage du dossier de financement OPCO</li>
              </ul>
              <a href="#contact" className="btn-primary" onClick={() => scrollToSection("contact", "Formation IA / agents sur-mesure")}>
                Demander un premier échange <ArrowRight size={18} />
              </a>
            </div>
          )}

          <div className="cta-banner">
            {offreTab === "subv" ? (
              <>
                <h3>La plupart des dirigeants ne demandent que les aides dont ils ont entendu parler</h3>
                <p>Les autres continuent d'exister sans eux. Un diagnostic gratuit suffit à savoir de quel côté vous êtes.</p>
                <a href="#contact" className="btn-cta" onClick={() => scrollToSection("contact", "Recherche de subventions")}>Demander mon diagnostic gratuit</a>
              </>
            ) : offreTab === "qualif" ? (
              <>
                <h3>{CTA_BANNIERE_QUALIF.titre}</h3>
                <p>{CTA_BANNIERE_QUALIF.texte}</p>
                <a href="#contact" className="btn-cta" onClick={() => scrollToSection("contact", "Qualification professionnelle")}>{CTA_BANNIERE_QUALIF.bouton}</a>
              </>
            ) : (
              <>
                <h3>Les marchés publics sont une mine d'opportunités</h3>
                <p>Chaque année, des milliards d'euros de marchés sont attribués à des entreprises qui ont su présenter la meilleure offre. Ne laissez plus ces opportunités passer.</p>
                <a href="#contact" className="btn-cta" onClick={() => scrollToSection("contact")}>Contactez-nous dès aujourd'hui</a>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Section « Témoignages » retirée le 31/07/2026 à la demande du client :
          les 3 témoignages et les 4 statistiques (95 %, 250+, 150+, 4,9/5) étaient
          des contenus générés à la création du site, sans réalité vérifiable.
          Ne rien remettre ici sans avis réels et autorisés par leurs auteurs. */}

      {/* ---------------------------------------------------------------- FAQ */}
      <section className="faq-section" id="faq" data-testid="faq-section">
        <div className="container">
          <span className="section-badge">Questions Fréquentes</span>
          <h2>Vos questions, nos réponses</h2>
          <p className="section-subtitle">Découvrez les réponses aux questions les plus fréquemment posées</p>

          <div className="faq-list">
            {faqs.map((faq, index) => {
              const nouveauGroupe = index === 0 || faqs[index - 1].groupe !== faq.groupe;
              return (
                <div key={index}>
                  {nouveauGroupe && <p className="faq-group">{faq.groupe}</p>}
                  <div className={`faq-item ${openFaq === index ? "open" : ""}`}>
                    <button
                      className="faq-question"
                      onClick={() => toggleFaq(index)}
                      aria-expanded={openFaq === index}
                      aria-controls={`faq-answer-${index}`}
                      data-testid={`faq-${index}`}
                    >
                      <span><span className="faq-number">{index + 1}</span> {faq.question}</span>
                      <ChevronDown className={`faq-chevron ${openFaq === index ? "rotated" : ""}`} />
                    </button>
                    {openFaq === index && (
                      <div className="faq-answer" id={`faq-answer-${index}`}>
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="faq-cta">
            <h3>Vous avez d'autres questions ?</h3>
            <p>Notre équipe est à votre disposition pour répondre à toutes vos interrogations.</p>
            <a href="#contact" className="btn-primary" onClick={() => scrollToSection("contact")}>Contactez-nous</a>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ Contact */}
      <section className="contact-section" id="contact" data-testid="contact-section">
        <div className="container">
          <h2>Parlons de votre projet</h2>
          <p className="section-subtitle">Un marché à décrocher, du temps à récupérer — ou les deux. Écrivez-nous.</p>

          <div className="contact-grid">
            <div className="contact-form-container">
              <h3>Demande de devis</h3>
              <p>Décrivez votre besoin, nous revenons vers vous sous 24 h ouvrées</p>
              <form onSubmit={handleDevisSubmit} data-testid="contact-form">
                <div className="form-group">
                  <label htmlFor="o-name">Nom complet *</label>
                  <input id="o-name" type="text" required autoComplete="name"
                    value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="o-email">Email *</label>
                  <input id="o-email" type="email" required autoComplete="email"
                    value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="o-phone">Téléphone *</label>
                  <input id="o-phone" type="tel" required autoComplete="tel"
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="o-company">Entreprise *</label>
                  <input id="o-company" type="text" required autoComplete="organization"
                    value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="o-sector">Secteur d'activité *</label>
                  <select id="o-sector" required value={formData.sector}
                    onChange={(e) => setFormData({ ...formData, sector: e.target.value })}>
                    <option value="">Sélectionnez votre secteur</option>
                    <option value="Bâtiment & Travaux Publics">Bâtiment &amp; Travaux Publics</option>
                    <option value="Informatique & Numérique">Informatique &amp; Numérique</option>
                    <option value="Nettoyage & Services">Nettoyage &amp; Services</option>
                    <option value="Sécurité & Surveillance">Sécurité &amp; Surveillance</option>
                    <option value="Conseil & Formation">Conseil &amp; Formation</option>
                    <option value="Fournitures & Équipements">Fournitures &amp; Équipements</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                <label className="form-consent">
                  <input type="checkbox" required checked={formData.consent}
                    onChange={(e) => setFormData({ ...formData, consent: e.target.checked })} />
                  <span>
                    J'accepte que mes données soient utilisées pour être recontacté au sujet de ma
                    demande. Elles ne sont ni cédées ni revendues —{" "}
                    <a href="/confidentialite" target="_blank" rel="noopener noreferrer">politique de confidentialité</a>.
                  </span>
                </label>
                <button type="submit" className="btn-primary full-width" disabled={status?.form === "devis" && status?.state === "sending"} data-testid="submit-devis">
                  <Send size={16} /> Demander mon devis
                </button>
                <StatusMessage form="devis" />
              </form>
            </div>

            <div className="contact-form-container">
              <h3>Demande de rendez-vous</h3>
              <p>Appels d'offres, subventions, qualifications, IA sur-mesure ou financement</p>
              <form onSubmit={handleRdvSubmit} data-testid="rdv-form">
                <div className="form-group">
                  <label htmlFor="r-objet">Objet de votre demande *</label>
                  <select id="r-objet" required value={rdvFormData.objet}
                    onChange={(e) => setRdvFormData({ ...rdvFormData, objet: e.target.value })}>
                    {OBJETS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="r-name">Nom complet *</label>
                  <input id="r-name" type="text" required autoComplete="name"
                    value={rdvFormData.name} onChange={(e) => setRdvFormData({ ...rdvFormData, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="r-email">Email *</label>
                  <input id="r-email" type="email" required autoComplete="email"
                    value={rdvFormData.email} onChange={(e) => setRdvFormData({ ...rdvFormData, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="r-phone">Téléphone *</label>
                  <input id="r-phone" type="tel" required autoComplete="tel"
                    value={rdvFormData.phone} onChange={(e) => setRdvFormData({ ...rdvFormData, phone: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="r-company">Entreprise *</label>
                  <input id="r-company" type="text" required autoComplete="organization"
                    value={rdvFormData.company} onChange={(e) => setRdvFormData({ ...rdvFormData, company: e.target.value })} />
                </div>
                <div className="form-group">
                  <label htmlFor="r-message">Message</label>
                  <textarea id="r-message" rows="4" value={rdvFormData.message}
                    onChange={(e) => setRdvFormData({ ...rdvFormData, message: e.target.value })} />
                </div>
                <label className="form-consent">
                  <input type="checkbox" required checked={rdvFormData.consent}
                    onChange={(e) => setRdvFormData({ ...rdvFormData, consent: e.target.checked })} />
                  <span>
                    J'accepte que mes données soient utilisées pour être recontacté au sujet de ma
                    demande. Elles ne sont ni cédées ni revendues —{" "}
                    <a href="/confidentialite" target="_blank" rel="noopener noreferrer">politique de confidentialité</a>.
                  </span>
                </label>
                <button type="submit" className="btn-primary full-width" disabled={status?.form === "rdv" && status?.state === "sending"} data-testid="submit-rdv">
                  <Send size={16} /> Envoyer la demande
                </button>
                <StatusMessage form="rdv" />
              </form>
            </div>

            <div className="contact-info">
              <div className="contact-info-item">
                <div className="contact-icon-wrapper"><Mail className="contact-icon" /></div>
                <div>
                  <h4>Email</h4>
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-icon-wrapper"><Phone className="contact-icon" /></div>
                <div>
                  <h4>Téléphone</h4>
                  <a href={`tel:${CONTACT_PHONE_INTL}`} data-testid="phone-number">{CONTACT_PHONE}</a>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-icon-wrapper"><MapPin className="contact-icon" /></div>
                <div>
                  <h4>Localisation</h4>
                  <span>{SOCIETE.zoneIntervention}<br /><small>(siège social à Barcelone)</small></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3><span className="logo-text">Hupsoo</span> <span className="logo-accent">Conseil</span></h3>
              <p className="footer-legal-name">Nom commercial de {SOCIETE.denomination}</p>
              <p>{SOCIETE.adresse}</p>
              <p>N.I.F. {SOCIETE.nif} · {SOCIETE.registre}</p>
              <p><strong>Zone d'intervention : {SOCIETE.zoneIntervention}</strong></p>
            </div>

            <div className="footer-links">
              <h4>Nos expertises</h4>
              <a href="#expertise" onClick={() => scrollToSection("expertise")}>Réponse aux appels d'offres</a>
              <a href="#subventions" onClick={() => scrollToSection("subventions")}>Recherche de subventions</a>
              <a href="#qualifications" onClick={() => scrollToSection("qualifications")}>Qualifications professionnelles</a>
              <a href="#ia-sur-mesure" onClick={() => scrollToSection("ia-sur-mesure")}>IA sur-mesure</a>
              <a href="#offres" onClick={() => scrollToSection("offres")}>Nos offres</a>
              <a href="/reponse-appel-offres">Guide des appels d'offres</a>
              <a href="/recherche-subventions-entreprise">Guide des subventions</a>
              <a href="/accompagnement-qualification-qualibat-rge">Guide des qualifications</a>
              <a href="/creer-agent-ia">Créer un agent IA</a>
              <a href="/appels-offres-montpellier">Montpellier &amp; Hérault</a>
              <a href="/appels-offres-perpignan">Perpignan &amp; P.-O.</a>
              <a href="/aides-subventions-occitanie">Aides en Occitanie</a>
            </div>

            <div className="footer-contact">
              <h4>Nous contacter</h4>
              <p className="footer-mentions">
                Les actions de formation sont réalisées avec des organismes de formation partenaires
                certifiés Qualiopi, sélectionnés selon votre situation et votre financeur.
              </p>
              <p><Mail size={16} /> {CONTACT_EMAIL}</p>
              <p><Phone size={16} /> <a href={`tel:${CONTACT_PHONE_INTL}`} data-testid="footer-phone">{CONTACT_PHONE}</a></p>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} Hupsoo Conseil — {SOCIETE.denomination}. Tous droits réservés.</p>
            <p className="footer-bottom-links">
              <a href="/mentions-legales">Mentions légales</a>
              <a href="/confidentialite">Politique de confidentialité</a>
            </p>
          </div>
        </div>
      </footer>

      {/* ------------------------------------------------- Barre mobile fixe */}
      <div className={`sticky-cta ${showStickyBar ? "visible" : ""}`} data-testid="sticky-cta">
        <a href={`tel:${CONTACT_PHONE_INTL}`} className="sticky-call"><Phone size={18} /> Appeler</a>
        <a href="#contact" className="sticky-rdv" onClick={() => scrollToSection("contact")}>Prendre RDV</a>
      </div>
    </div>
  );
}

export default App;
